import { memo, useCallback } from "react";
import { useNavigate } from "react-router-dom";

const SERVER_BASE_URL = "https://admin.monsinidress.com";

function getProductImage(product) {
	const image = product?.images?.[0];
	if (!image) return "";
	if (/^(https?:\/\/|data:|blob:)/i.test(image)) return image;
	return `${SERVER_BASE_URL}/${String(image).replace(/^\/+/, "")}`;
}

const SearchIcon = () => (
	<svg
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="2"
		strokeLinecap="round"
		strokeLinejoin="round"
		className="h-7 w-7"
		aria-hidden="true"
	>
		<circle cx="11" cy="11" r="7" />
		<path d="m20 20-4-4" />
	</svg>
);

const CloseIcon = () => (
	<svg
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="2"
		strokeLinecap="round"
		className="h-7 w-7"
		aria-hidden="true"
	>
		<path d="M6 6l12 12M18 6 6 18" />
	</svg>
);

function Search({
	len,
	setSearch,
	search,
	filteredData,
	searchbtn,
	searchRef,
	setSearchBtn,
	loading,
	error,
}) {
	const navigate = useNavigate();
	const normalizedSearch = search.trim();

	const handleClose = useCallback(() => {
		setSearch("");
		setSearchBtn(false);
	}, [setSearch, setSearchBtn]);

	const handleProductClick = useCallback(
		(product) => {
			const style = String(product?.style || "").trim();
			if (!style) return;
			handleClose();
			navigate(`/product/id/${encodeURIComponent(style)}`, {
				state: { product },
			});
		},
		[handleClose, navigate]
	);

	if (!searchbtn) return null;

	return (
		<div
			className="fixed inset-0 z-[10000] overflow-y-auto bg-white"
			role="dialog"
			aria-modal="true"
			aria-label="Search products"
		>
			<div
				ref={searchRef}
				className="mx-auto min-h-screen w-full max-w-[1600px] px-4 pb-12 pt-6 md:px-10 md:pt-10 lg:px-16"
			>
				<div className="grid grid-cols-[1fr_auto] items-center gap-5 md:grid-cols-[220px_1fr_auto] lg:gap-10">
					<div className="relative hidden w-fit md:block">
						<div className="myclass relative text-5xl font-medium leading-tight tracking-tight text-gray-800">
							Monsini
							<span className="absolute bottom-0 right-0 whitespace-nowrap font-poppins text-[10px] font-bold leading-3 text-red-700">
								PROUDLY CANADIAN
							</span>
						</div>
					</div>

					<div className="relative min-w-0">
						<input
							type="search"
							value={search}
							onChange={(event) => setSearch(event.target.value)}
							placeholder="Search by style number..."
							autoFocus
							aria-label="Search by style number"
							className="w-full rounded-md border-2 border-black/30 px-4 py-3 pr-14 font-Jost-Regular text-lg outline-none transition focus:border-black"
						/>
						<span className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-black">
							<SearchIcon />
						</span>
					</div>

					<button
						type="button"
						onClick={handleClose}
						className="rounded-full p-2 text-black transition hover:bg-gray-100"
						aria-label="Close search"
					>
						<CloseIcon />
					</button>
				</div>

				<div className="mt-8 border-t border-gray-100 pt-7">
					{normalizedSearch.length < 2 && (
						<p className="text-center font-poppins text-gray-500">
							Enter at least 2 characters to search products.
						</p>
					)}

					{normalizedSearch.length >= 2 && loading && (
						<div className="flex items-center justify-center gap-3 py-16">
							<div className="h-8 w-8 animate-spin rounded-full border-4 border-pink-100 border-t-pink-500" />
							<p className="font-poppins text-gray-500">Loading products...</p>
						</div>
					)}

					{normalizedSearch.length >= 2 && !loading && error && (
						<p className="py-10 text-center font-poppins text-red-700">{error}</p>
					)}

					{normalizedSearch.length >= 2 && !loading && !error && len > 0 && (
						<>
							<p className="mb-5 font-poppins text-sm text-gray-500">
								{len} {len === 1 ? "product" : "products"} found
							</p>
							<div className="grid grid-cols-1 gap-5 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4">
								{filteredData.map((product, index) => {
									const image = getProductImage(product);
									return (
										<button
											type="button"
											key={`${product?._id || product?.id || product?.style}-${index}`}
											onClick={() => handleProductClick(product)}
											className="group overflow-hidden rounded-md bg-white text-left shadow-sm ring-1 ring-gray-200 transition hover:-translate-y-1 hover:shadow-lg hover:ring-pink-300"
										>
											<div className="aspect-[4/5] w-full overflow-hidden bg-gray-100">
												{image ? (
													<img
														src={image}
														alt={`Style ${product?.style || "product"}`}
														loading="lazy"
														decoding="async"
														width="350"
														height="438"
														className="h-full w-full object-cover object-top transition duration-500 group-hover:scale-[1.03]"
													/>
												) : (
													<div className="flex h-full items-center justify-center text-sm text-gray-400">
														Image unavailable
													</div>
												)}
											</div>
											<div className="p-4">
												<p className="font-lora text-lg font-medium text-gray-900">
													Style {product?.style}
												</p>
												<p className="mt-1 line-clamp-1 font-poppins text-sm text-gray-500">
													{product?.category || product?.type || "Monsini"}
												</p>
											</div>
										</button>
									);
								})}
							</div>
						</>
					)}

					{normalizedSearch.length >= 2 && !loading && !error && len === 0 && (
						<div className="py-16 text-center">
							<p className="font-lora text-2xl text-gray-800">No products found</p>
							<p className="mt-2 font-poppins text-sm text-gray-500">
								Try another style number.
							</p>
						</div>
					)}
				</div>
			</div>
		</div>
	);
}

export default memo(Search);
